import { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n";
import { fmtSAR, monthlyPayment } from "../lib/format";

const Row = ({ id, label, value, onChange, min, max, step }) => {
  const [raw, setRaw] = useState(String(value));
  useEffect(() => setRaw(String(value)), [value]);
  const commit = () => {
    const n = Number(raw);
    const v = Number.isFinite(n) && raw.trim() !== "" ? Math.min(max, Math.max(min, n)) : value;
    setRaw(String(v));
    onChange(v);
  };
  const live = (s) => {
    setRaw(s);
    const n = Number(s);
    if (s.trim() !== "" && Number.isFinite(n) && n >= min && n <= max) onChange(n);
  };
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-xs uppercase tracking-wider text-fg/60">{label}</label>
        <input id={`${id}-num`} aria-label={label} data-testid={`${id}-input`} type="text" inputMode="decimal" value={raw}
          onChange={(e) => live(e.target.value.replace(/[^\d.]/g, ""))} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && commit()} className="field !w-36 !py-1.5 text-end" dir="ltr" />
      </div>
      <input id={id} data-testid={`${id}-slider`} type="range" min={min} max={max} step={step} value={Math.min(max, Math.max(min, value))} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-cta" />
    </div>
  );
};

export default function MortgageCalculator({ price: initial }) {
  const { t, lang } = useI18n();
  const [price, setPrice] = useState(initial);
  const [down, setDown] = useState(10);
  const [rate, setRate] = useState(5.5);
  const [years, setYears] = useState(25);
  const r = monthlyPayment(price, down, rate, years);
  return (
    <section data-testid="mortgage-calculator" className="glass rounded-sm p-6 md:p-8" aria-labelledby="mortgage-title">
      <h2 id="mortgage-title" className="h-display mb-6 text-2xl md:text-3xl">{t("mortgage.title")}</h2>
      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-5">
          <Row id="mortgage-price" label={t("mortgage.price")} value={price} onChange={setPrice} min={100000} max={Math.max(50000000, initial)} step={10000} />
          <Row id="mortgage-down" label={t("mortgage.down")} value={down} onChange={setDown} min={0} max={90} step={1} />
          <Row id="mortgage-rate" label={t("mortgage.rate")} value={rate} onChange={setRate} min={0} max={15} step={0.1} />
          <Row id="mortgage-years" label={t("mortgage.years")} value={years} onChange={setYears} min={1} max={30} step={1} />
        </div>
        <div className="flex flex-col justify-center rounded-sm bg-brand/60 p-6">
          <p className="eyebrow">{t("mortgage.monthly")}</p>
          <p data-testid="mortgage-monthly-result" className="mt-3 font-display text-4xl text-fg md:text-5xl">{fmtSAR(Math.round(r.monthly), lang)}</p>
          <dl className="mt-6 space-y-2 border-t border-fg/15 pt-4 text-sm">
            <div className="flex justify-between"><dt className="text-fg/60">{t("mortgage.loan")}</dt><dd data-testid="mortgage-loan-amount">{fmtSAR(Math.round(r.principal), lang)}</dd></div>
            <div className="flex justify-between"><dt className="text-fg/60">{t("mortgage.interest")}</dt><dd data-testid="mortgage-total-interest">{fmtSAR(Math.round(r.interest), lang)}</dd></div>
          </dl>
        </div>
      </div>
      <p data-testid="mortgage-disclaimer" className="mt-6 text-xs text-fg/50">{t("mortgage.disclaimer")}</p>
    </section>
  );
}
